import { defineStore } from 'pinia';
import { ref, computed, readonly } from 'vue';

/**
 * Time Entry Form State Management Store
 * 
 * PURPOSE:
 * This store handles form state persistence during panel ↔ modal transitions in the time entry system.
 * It prevents data loss when users switch between the slide panel and full modal views.
 * 
 * KEY FEATURES:
 * - Deep cloning of form data to prevent reactivity issues
 * - Transition context tracking for debugging and flow control
 * - Session-based state management to prevent cross-entry contamination
 * - Auto-cleanup after successful operations
 * 
 * USAGE FLOW:
 * 1. User fills out form in panel/modal
 * 2. User clicks expand/minimize button
 * 3. Form state is saved with transition context
 * 4. Target component loads and restores the saved state
 * 5. State is cleared after successful restoration or save
 * 
 * DEVELOPER NOTES:
 * - Always use structuredClone for deep copying when available
 * - Transition context helps debug which component should handle cleanup
 * - The store automatically handles Vue reactivity isolation
 */
export const useTimeEntryFormStore = defineStore('timeEntryForm', () => {
  // Private state - prefixed with _ to indicate internal use only
  const _formState = ref(null); // Stores the actual form data during transitions
  const _transitionContext = ref(null); // Metadata about the transition (source, type, timing)
  const _isTransitioning = ref(false); // Flag to prevent operations during active transitions

  // Public computed properties for read-only access
  // These ensure external components can't accidentally mutate internal state
  const hasFormState = computed(() => Boolean(_formState.value));
  const isTransitioning = computed(() => _isTransitioning.value);
  const transitionContext = computed(() => readonly(_transitionContext.value));

  // Actions - Public methods for managing form state

  /**
   * Saves form data during panel ↔ modal transitions
   * 
   * @param {Object} formData - Complete form state to preserve
   * @param {Object} context - Transition metadata for debugging and flow control
   * @param {string} context.type - Type of transition: 'expand', 'minimize', 'auto-save', 'initial'
   * @param {string} context.sourceComponent - Which component initiated the save: 'panel' or 'modal'
   * @param {string} context.entryType - Whether this is a 'NEW ENTRY' or 'EXISTING ENTRY'
   * @param {string|null} context.entryId - Actual entry ID (null for new entries)
   * @param {string} context.sessionKey - Unique session identifier to prevent cross-entry contamination
   * 
   * @returns {boolean} Success status
   * 
   * IMPORTANT: Uses structuredClone for deep copying to prevent Vue reactivity issues
   */
  const saveFormState = (formData, context = {}) => {
    if (!formData) {
      console.warn('[TimeEntryFormStore] Attempted to save null/undefined form state');
      return false;
    }
    try {
      // Deep clone to prevent reactivity issues and mutations
      // structuredClone is the modern standard, fallback to JSON for older browsers

      _formState.value = typeof structuredClone === 'function' ? structuredClone(formData) : JSON.parse(JSON.stringify(formData));
      _transitionContext.value = {
        type: context.type || 'unknown',
        timestamp: Date.now(),
        sourceComponent: context.sourceComponent || 'unknown',
        entryType: context.entryType || 'unknown',
        entryId: context.entryId || null,
        sessionKey: context.sessionKey || null
      };
      _isTransitioning.value = true;
      return true;
    } catch (error) {
      console.error('[TimeEntryFormStore] Failed to save form state:', error);
      return false;
    }
  };

  /**
   * Retrieves the current saved form state
   * 
   * @returns {Object|null} The saved form data, or null if nothing is saved
   * 
   * NOTE: Returns the actual stored reference - caller should treat as read-only
   * or clone if mutations are needed
   */
  const getFormState = () => {
    const state = _formState.value;
    return state;
  };

  /**
   * Clears all saved form state and transition context
   * 
   * USAGE:
   * - Called after successful form restoration
   * - Called when user explicitly closes/cancels
   * - Called when switching to a different entry to prevent contamination
   * - Called after successful save to prevent stale data
   * 
   * This is a critical cleanup method to prevent old data from appearing
   * in new forms or different entries.
   */
  const clearFormState = () => {
    const hadState = Boolean(_formState.value);
    _formState.value = null;
    _transitionContext.value = null;
    _isTransitioning.value = false;
  };

  /**
   * Marks a transition as complete without clearing the saved state
   * 
   * Used in scenarios where the state should persist but the transition
   * flag needs to be reset (rare use case).
   */
  const completeTransition = () => {
    if (_isTransitioning.value) {
      _isTransitioning.value = false;
    }
  };

  // Computed helpers for specific transition types
  // These make it easy to check what kind of transition is in progress

  /**
   * True when the current transition is modal → panel (minimize)
   * Used by components to determine if they should handle cleanup
   */
  const isMinimizeTransition = computed(() => _transitionContext.value?.type === 'minimize');

  /**
   * True when the current transition is panel → modal (expand)
   * Used by components to determine if they should handle cleanup
   */
  const isExpandTransition = computed(() => _transitionContext.value?.type === 'expand');

  // Public API - Everything external components need
  return {
    // Public readonly state - computed properties prevent external mutation
    hasFormState,
    // Boolean: Is there saved form data?
    isTransitioning,
    // Boolean: Is a transition currently in progress?
    transitionContext,
    // Object: Metadata about the current/last transition
    isMinimizeTransition,
    // Boolean: Is this a modal → panel transition?
    isExpandTransition,
    // Boolean: Is this a panel → modal transition?

    // Actions - Methods for managing form state
    saveFormState,
    // Save form data with transition context
    getFormState,
    // Retrieve saved form data
    clearFormState,
    // Clear all saved data (most common cleanup)
    completeTransition // Mark transition complete without clearing data
  };
});